import React, {
  forwardRef,
  useContext,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from "react";
import { createPortal } from "react-dom";
import { cn } from "../../styles";
import { LocalProp, ThemeColor } from "../../props";
import { themeTextStyles } from "../../styles";
import {
  LocalizationContext,
  unwrapIcon,
  useTranslate,
} from "../../util/localization";
import { useElementBounds } from "../../helper/useElementBounds";
import { IconBaseProps } from "./types";

export interface FontIconProps
  extends IconBaseProps,
    Omit<React.HTMLAttributes<HTMLSpanElement>, "color"> {
  name: string;
  local?: LocalProp;
}

const Inner = forwardRef<HTMLSpanElement, FontIconProps>(
  (
    {
      className = "",
      style,
      name,
      local,
      color = ThemeColor.none,
      size = 20,
      muted = false,
      ...props
    },
    ref
  ) => {
    const hasCustomSize =
      /\bw-(\[.*\]|\d+)\b/.test(className) ||
      /\bh-(\[.*\]|\d+)\b/.test(className) ||
      (style && (style.width !== undefined || style.height !== undefined));

    return (
      <span
        ref={ref}
        className={cn(
          "inline-grid shrink-0 place-items-center align-middle leading-none",
          "[container-type:size]",
          muted && "opacity-50",
          className
        )}
        style={{
          ...(hasCustomSize ? {} : { width: `${size}px`, height: `${size}px` }),
          ...style,
        }}
        {...props}
      >
        <span
          className={cn(
            "block shrink-0 leading-none",
            "text-[100cqmin]",
            themeTextStyles[color],
            "mdi",
            `mdi-${name}`
          )}
          style={hasCustomSize ? {} : { fontSize: `${size}px` }}
          aria-hidden="true"
        />
      </span>
    );
  }
);

// Utility: Check if element is visually covered
const isElementCovered = (
  element: HTMLElement,
  backdropElement: HTMLElement
): boolean => {
  if (!element) return true;

  const rect = element.getBoundingClientRect();
  if (rect.width <= 0 || rect.height <= 0) return true;

  const tolerance = 10;
  const points = [
    { x: rect.left + tolerance, y: rect.top + tolerance },
    { x: rect.right - tolerance, y: rect.top + tolerance },
    { x: rect.left + tolerance, y: rect.bottom - tolerance },
    { x: rect.right - tolerance, y: rect.bottom - tolerance },
  ];

  const portalElements = Array.from(
    document.getElementsByClassName("pss-localization-item")
  );

  const originalDisplay = backdropElement.style.display;
  const originalPortalDisplays = portalElements.map(
    (el) => (el as HTMLElement).style.display
  );

  backdropElement.style.display = "none";
  portalElements.forEach((el) => ((el as HTMLElement).style.display = "none"));

  const isCovered = points.some((point) => {
    const topElement = document.elementFromPoint(point.x, point.y);
    return (
      topElement && topElement !== element && !element.contains(topElement)
    );
  });

  backdropElement.style.display = originalDisplay;
  portalElements.forEach(
    (el, i) => ((el as HTMLElement).style.display = originalPortalDisplays[i])
  );

  return isCovered;
};

// Round bounds to prevent subpixel misalignment
function roundBounds(bounds: {
  top: number;
  left: number;
  width: number;
  height: number;
}) {
  return {
    top: Math.round(bounds.top) + 1, // Shift down slightly
    left: Math.round(bounds.left),
    width: Math.round(bounds.width),
    height: Math.round(bounds.height) - 1, // Slightly smaller to compensate
  };
}

// Extracted style copier
function getStyleClone(source: HTMLElement, backdropElement: HTMLElement) {
  try {
    const styles = window.getComputedStyle(source);
    const isVisible =
      styles.display !== "none" &&
      styles.visibility !== "hidden" &&
      styles.opacity !== "0" &&
      source.offsetParent !== null &&
      !isElementCovered(source, backdropElement);

    return {
      display: isVisible ? "block" : "none",
      fontFamily: styles.fontFamily,
      fontSize: styles.fontSize,
      fontWeight: styles.fontWeight,
      lineHeight: styles.lineHeight,
      color: "#fff",
      textAlign: styles.textAlign as React.CSSProperties["textAlign"],
      textTransform:
        styles.textTransform as React.CSSProperties["textTransform"],
      letterSpacing: styles.letterSpacing,
      textDecoration: styles.textDecoration,
      fontStyle: styles.fontStyle,
      backgroundColor: styles.backgroundColor,
      border: styles.border,
      borderRadius: styles.borderRadius,
      boxShadow: styles.boxShadow,
      padding: styles.padding,
      margin: 0,
    };
  } catch (e) {
    console.warn("Failed to get computed styles:", e);
    return {};
  }
}

export const FontIcon = forwardRef<HTMLSpanElement, FontIconProps>(
  ({ name, local, ...props }, ref) => {
    const { edit } = useContext(LocalizationContext) || {};
    const [t, { prefixes }] = useTranslate(edit?.state.stringOverrides) as any;
    const anchorRef = useRef<HTMLElement | null>(null);
    const [preview, setPreview] = useState<string | null>(null);
    const [elementRef, bounds, updateBounds] = useElementBounds(
      !!edit,
      edit?.backdropElement
    );
    useImperativeHandle(ref, () => elementRef.current || null);

    useEffect(() => {
      if (edit?.state.editingClaim?.anchorEle === anchorRef.current) {
        setPreview(edit?.state.preview);
      } else if (preview) {
        setPreview(null);
      }
      requestAnimationFrame(updateBounds);
    }, [edit?.state.preview, updateBounds, edit?.backdropElement]);

    if (!local && !name) return null;

    let localKey = null;
    if (typeof local === "object" && local.key) localKey = local.key;
    if (typeof local === "string") localKey = local;
    if (local === true) localKey = name;

    if (!t && local) {
      console.error(
        `Tried to render localized Icon outside of localization context: ${localKey}`
      );
      return <Inner name="alert-rhombus-outline" {...props} ref={elementRef} />;
    }

    if (!localKey) return <Inner name={name} {...props} ref={elementRef} />;

    const localized =
      preview ||
      t(
        localKey,
        typeof local === "object" ? local.context : {},
        typeof local === "object"
          ? { ...local.opts, noFallback: true }
          : { noFallback: true }
      );

    const iconData = unwrapIcon(localized);
    const finalName =
      iconData?.name || localized || name || "alert-rhombus-outline";

    return (
      <>
        <Inner name={finalName} {...props} ref={elementRef} />

        {edit &&
          bounds &&
          createPortal(
            <Inner
              name={finalName}
              {...props}
              ref={anchorRef}
              onClick={(e) => {
                edit.actions.claimEditing({
                  anchorEle: e.target,
                  prefixes,
                  local,
                });
                e.stopPropagation();
              }}
              className={cn(
                "pss-localization-item",
                "hover:before:absolute hover:before:top-[-3px] hover:before:left-[-2px] hover:before:right-[-2px] hover:before:bottom-[-2px]",
                "hover:before:border-2 hover:before:border-primary-500",
                "hover:before:rounded cursor-pointer relative"
              )}
              style={{
                position: "absolute",
                ...roundBounds(bounds),
                boxSizing: "border-box",
                ...(elementRef.current
                  ? getStyleClone(elementRef.current, edit.backdropElement)
                  : {}),
              }}
            />,
            edit.backdropElement
          )}
      </>
    );
  }
);
FontIcon.displayName = "FontIcon";
